<script setup lang="ts">
import { formatMoney } from "~/utils/formatters"
import { StickyBarProps } from "./StickyBar.props"

const props = withDefaults(defineProps<StickyBarProps>(), {
  isLoading: false,
  variant: "v1"
})

const emit =
  defineEmits<{
    (e: "update:quantity", value: number): void
    (e: "addToCart"): void
  }>()

const handleChangeQuantity = (newQuantity: number) => {
  emit("update:quantity", newQuantity)
}

const handleAddToCart = () => {
  emit("addToCart")
}

// Footer intersection detection - hide sticky bar when footer is visible
const isFooterVisible = ref(false)

onMounted(() => {
  const footer = document.querySelector(".footer")
  if (!footer) return

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        isFooterVisible.value = entry.isIntersecting
      })
    },
    {
      threshold: 0,
      rootMargin: "0px"
    }
  )

  observer.observe(footer)

  onUnmounted(() => {
    observer.disconnect()
  })
})

// Hide sticky bar when footer is visible
const shouldShow = computed(() => props.show && !isFooterVisible.value)
</script>

<template>
  <ClientOnly>
    <Transition name="slide-up">
      <div
        v-show="shouldShow"
        :class="variant"
        class="
          border-black-10
          bg-tiffany-10
          fixed
          bottom-0
          left-0
          right-0
          z-40
          border-t
          py-3
          shadow-[0_-4px_12px_rgba(0,0,0,0.1)]
        "
      >
        <div
          class="
            max-w-7xl
            padded
            mx-auto
            flex
            items-center
            justify-between
            gap-0
            md:justify-start
            md:gap-4
          "
        >
          <!-- Product Image + Info (wrapper) -->
          <div
            class="
              flex
              min-w-0
              flex-shrink-0
              items-center
              gap-2
              md:flex-1
              md:gap-4
            "
          >
            <!-- Product Image -->
            <div
              class="
                sticky-image
                h-[43px]
                w-[44px]
                flex-shrink-0
                overflow-hidden
                md:block
                md:h-[72px]
                md:w-[75px]
              "
            >
              <UtilsWrapperImage
                v-if="image"
                :image="image"
                :altText="title"
                class="h-full w-full object-cover"
              />
            </div>

            <!-- Product Info -->
            <div class="min-w-0 md:flex-1">
              <!-- Title (hidden on mobile) -->
              <p
                class="
                  beaver-bold
                  text-black-main
                  mb-2
                  line-clamp-1
                  hidden
                  md:block
                "
              >
                {{ title }}
              </p>
              <div
                class="flex flex-col gap-0 md:flex-row md:items-center md:gap-2"
              >
                <!-- Badge (hidden on mobile) -->
                <BadgeProduct
                  v-if="discount && discount > 0"
                  theme="SCONTO"
                  :text="`-${discount}%`"
                  class="mouse-bold text-white hidden md:inline-flex"
                />
                <span class="md:dolphin-bold beaver-bold text-black-main">
                  {{ formatMoney(price) }}
                </span>
                <span
                  v-if="oldPrice && oldPrice > price"
                  class="
                    md:mouse
                    snail
                    mouse-strikethrough
                    text-black-80
                    md:beaver
                  "
                >
                  {{ formatMoney(oldPrice) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Quantity Picker -->
          <div class="sticky-quantity flex-shrink-0">
            <UtilsQuantityPicker
              :quantity="quantity"
              :min="1"
              :max="maxQuantity"
              @update:modelValue="handleChangeQuantity"
              size="sm"
              class="bg-white ms-3"
            />
          </div>

          <!-- Add to Cart Button -->
          <UtilsButton
            @click="handleAddToCart"
            class="
              beaver-medium
              add-to-cart-btn
              ms-4
              w-full
              min-w-max
              md:ms-0
              md:w-auto
              md:flex-shrink-0 md:flex-grow-0
            "
            size="sm"
          >
            <template #icon>
              <span
                class="
                  relative
                  flex
                  w-full
                  items-center
                  justify-center
                  py-1
                  md:px-8
                "
              >
                <!-- Text always present, but invisible if it's loading (it keeps same button width) -->
                <span
                  :class="isLoading ? 'invisible' : 'flex items-center gap-1'"
                >
                  <span class="variant-text-long btn-content">{{
                    $t("productPage.purchaseType.organism.addToCart")
                  }}</span>
                  <span class="variant-text-short btn-content md:hidden">{{
                    $t("productPage.purchaseType.organism.add")
                  }}</span>
                  <span class="variant-icon btn-content md:hidden">
                    <UtilsIcon
                      name="ShoppingCartAdd.svg"
                      :altText="
                        $t('productPage.purchaseType.organism.addToCart')
                      "
                      class="h-6 w-6 cursor-pointer"
                      fetch-priority="high"
                      :preload="true"
                    />
                  </span>
                </span>
                <!-- loader on top, at the center -->
                <span
                  v-if="isLoading"
                  class="
                    absolute
                    left-1/2
                    top-1/2
                    -translate-x-1/2 -translate-y-1/2
                  "
                >
                  <UtilsLoader v-if="isLoading" class="h-4 w-4" />
                </span>
              </span>
            </template>
          </UtilsButton>
        </div>
      </div>
    </Transition>

    <!-- Spacer to prevent content from being hidden behind sticky bar -->
    <div v-if="shouldShow" class="h-[72px] md:h-[96px]" aria-hidden="true" />
  </ClientOnly>
</template>

<style scoped>

.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.slide-up-enter-to,
.slide-up-leave-from {
  transform: translateY(0);
  opacity: 1;
}

.btn-content {
  @apply hidden;
}

.v1 .variant-text-long {
  @apply inline;
}

.v2 .variant-text-short {
  @apply inline;
}

.v3 .variant-icon {
  @apply inline;
}

.sticky-quantity {
  @apply hidden;
}

.v2 .sticky-image {
  @apply hidden;
}

.v2 .sticky-quantity,
.v3 .sticky-quantity {
  @apply block;
}

/**
 * Force v3 button to shrink into a compact icon-only square on mobile.
 * The deep selectors override the library's internal <UtilsButton> paddings.
 */
@media (max-width: 1023px) {
  .v3 .add-to-cart-btn :deep(.btn__container),
  .v3 .add-to-cart-btn :deep(.relative.flex.w-full) {
    @apply w-auto px-1 py-1;
  }

  .v3 .add-to-cart-btn {
    @apply w-auto flex-initial flex-shrink-0;
  }
}

@screen md {
  .v2 .sticky-image {
    @apply block;
  }

  .sticky-quantity {
    @apply block;
  }

  /* On desktop always show the long label, regardless of variant */
  .v1 .variant-text-short,
  .v1 .variant-icon,
  .v2 .variant-text-short,
  .v2 .variant-icon,
  .v3 .variant-text-short,
  .v3 .variant-icon {
    @apply hidden;
  }

  .v1 .variant-text-long,
  .v2 .variant-text-long,
  .v3 .variant-text-long {
    @apply inline;
  }
}

</style>
